import { Check, Flame, Pencil, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { completeRoutineItem } from "wasp/client/operations";
import useTranslation from "../../../client/hooks/useTranslation";
import { Button } from "../../../client/components/ui/button";
import { toast } from "../../../client/hooks/use-toast";
import type { LiteRoutine } from "../../data/useLiteDiscipline";
import { completionDateFor, type ChecklistGroup } from "../../lib/discipline";
import { Bar, Card, Empty } from "../../ui/ui";

type Frequency = "weekly" | "monthly" | "custom";

/** Checklist du jour : tous les items des routines actives, groupés par routine. Cocher écrit immédiatement. */
export function RoutineTodayCard({ groups, total, streak, onAdd, onEdit }: {
  groups: ChecklistGroup<LiteRoutine>[];
  total: number;
  streak: number;
  onAdd: () => void;
  onEdit: (r: LiteRoutine) => void;
}) {
  const t = useTranslation().lite.discipline;
  const tt = useTranslation();
  const serverChecked = (g: ChecklistGroup<LiteRoutine>) => new Set(g.items.filter(i => i.done).map(i => i.label));
  // Surcharges optimistes éphémères par routine : le serveur reste la source de vérité,
  // la surcharge ne vit que le temps que le serveur ne la reflète pas encore.
  const [pending, setPending] = useState<Record<string, Set<string>>>({});
  const checkedSet = (g: ChecklistGroup<LiteRoutine>) => pending[g.routine.id] ?? serverChecked(g);
  const checked = (g: ChecklistGroup<LiteRoutine>, label: string) => checkedSet(g).has(label);

  // Retire une surcharge dès que le serveur la reflète (sinon un refetch périmé effacerait un clic en vol).
  useEffect(() => {
    setPending(p => {
      let changed = false; const next = { ...p };
      for (const g of groups) {
        const o = next[g.routine.id];
        if (!o) continue;
        const s = serverChecked(g);
        if (o.size === s.size && [...o].every(l => s.has(l))) { delete next[g.routine.id]; changed = true; }
      }
      return changed ? next : p;
    });
  }, [groups]);

  const done = groups.reduce((a, g) => a + g.items.filter(i => checked(g, i.label)).length, 0);
  const complete = total > 0 && done === total;

  const toggle = async (g: ChecklistGroup<LiteRoutine>, label: string) => {
    const next = new Set(checkedSet(g));
    if (next.has(label)) next.delete(label); else next.add(label);
    setPending(p => ({ ...p, [g.routine.id]: next }));
    try {
      // `date` explicite : sans elle, un clic à 00 h 30 à Paris écrirait (et écraserait) la veille côté serveur UTC.
      await completeRoutineItem({ routineId: g.routine.id, completedItems: [...next], replace: true, date: completionDateFor() });
    } catch (e) {
      console.error(e);
      setPending(p => { const n = { ...p }; delete n[g.routine.id]; return n; });
      toast({ title: tt.common.error, variant: "destructive" });
    }
  };

  const addBtn = <Button size="sm" variant="outline" onClick={onAdd}><Plus className="h-4 w-4 mr-1" />{t.routine.add}</Button>;

  if (!groups.length) {
    return (
      <Card title={t.routine.title} action={addBtn}>
        <Empty title={t.routine.emptyTitle} action={<Button onClick={onAdd}><Plus className="h-4 w-4 mr-1.5" />{t.routine.emptyCta}</Button>}>{t.routine.emptyText}</Empty>
      </Card>
    );
  }

  return (
    <Card title={t.routine.title} subtitle={t.routine.progress(done, total)} action={addBtn}>
      <div className="flex items-center gap-3">
        <Bar value={total ? done / total : 0} tone={complete ? "gain" : "brand"} className="flex-1" />
        {streak > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap"><Flame size={13} className="text-warning" />{t.routine.streak(streak)}</span>
        )}
      </div>
      {complete && <p className="text-xs text-pnlPositive mt-2">{t.routine.allDone}</p>}
      <div className="mt-4 space-y-5">
        {groups.map(g => (
          <div key={g.routine.id}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground">
                {g.routine.name}
                {g.routine.frequency !== "daily" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted">{t.routine.frequency[g.routine.frequency as Frequency] ?? g.routine.frequency}</span>
                )}
              </span>
              <button type="button" onClick={() => onEdit(g.routine)} className="w-7 h-7 grid place-items-center rounded-lg hover:bg-muted text-muted-foreground" aria-label={t.routine.edit}><Pencil size={13} /></button>
            </div>
            <ul className="space-y-1">
              {g.items.map(it => {
                const on = checked(g, it.label);
                return (
                  <li key={it.label}>
                    <button type="button" onClick={() => toggle(g, it.label)} className="w-full flex items-center gap-3 rounded-xl px-2 py-2 text-left text-sm hover:bg-muted/60 transition-colors" aria-pressed={on}>
                      <span className={`w-5 h-5 rounded-full grid place-items-center shrink-0 border transition-colors ${on ? "bg-pnlPositive border-pnlPositive text-background" : "border-border"}`}>{on && <Check size={12} />}</span>
                      <span className={on ? "line-through text-muted-foreground" : ""}>{it.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}
